import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { map, lastValueFrom, of, delay, firstValueFrom } from 'rxjs';
import { ProductionPriceList, ProductionPriceListForm } from '../../state';
import { PriceListForm } from 'src/state/price-list-form/state-models/price-list-form';
import { CommonApiService } from '../common.service';
import { ProductionPriceCondition, variantCodeValues } from 'src/state/production-price-list/state-models/production-price-condition';


@Injectable({
  providedIn: 'root'
})
export class ProductionPriceListApiService {
  constructor(private apollo: Apollo,
    private commonService: CommonApiService) { }

  public async getProductionPriceConditions(
    variables: any
  ): Promise<{ list: ProductionPriceCondition[]; totalCount: number }> {
    const query = gql`
      query priceListQuery(
        $id: Int,
        $paging: Paging,
        $order: String,
        $filter: String,
        $showOnlyValidConditions: Boolean
      ) {
        productionPriceLists {
          priceListCondition(id: $id, paging: $paging, order: $order, filter: $filter, showOnlyValidConditions: $showOnlyValidConditions) {
            list {
             
              id
              description
              priceSummary
              variantCodeSummary
              validFrom
              validTo
              listOfLastModifiedBy
              variantCodeValues {
                key
                value
              }

              productionPriceConditionVariantOption {
                variantOption {
                  value
                  variantFamily {
                    value
                    variantClass {
                      value
                    }
                  }
                }
              }

              productionPriceConditionVariantFamily {
                variantFamily {
                  value
                  variantClass {
                    value
                  }
                }
              }

              productionPriceConditionPrice {
                price
                productionPriceType {
                  id
                  name
                  description
                }
              }
            }
            totalCount
          }
        }
      }
    `;

    const result$ = this.apollo.query({
      query,
      variables,
      fetchPolicy: 'no-cache'
    });

    const mapped$ = result$.pipe(
      map((res: any) => {
        
        const result = res?.data?.productionPriceLists?.priceListCondition ?? { list: [], totalCount: 0 };

        const list: ProductionPriceCondition[] = (result.list ?? []).filter(Boolean);

        const totalCount: number = result.totalCount ?? 0;
        return { list, totalCount };
      })
    );

    return lastValueFrom(mapped$);
  }


  getAllPriceLists(): Promise<ProductionPriceList[]> {
    const query = gql`
    query priceListQuery {
              productionPriceLists {
                priceList {

                  list {
                    id
                    description 
                    componentType {          
                        name
                        code               
                      }        
                    market {   
                      name 
                    }
                    productCalculationType {
                      name
                      description
                    } 
                  }
                     totalCount
                }
              }
            }
    `;
    const result$ = this.apollo
      .query<ProductionPriceList[]>({
        query: query
      })
      .pipe<ProductionPriceList[]>(
        map((result: any) => {
          return result?.data?.productionPriceLists?.priceList?.list;
        })
      )
    return lastValueFrom(result$);

  }

  exportSelectedProductionPriceList(productionPriceListId?: number, fileName?: string) {
    const query = gql`
        query priceListQuery${!!productionPriceListId ? '($id: Int)' : ''} 
	    {
            productionPriceLists 
            {
                search${!!productionPriceListId ? '(id: $id)' : ''} 
                {                                                            
                    componentType
                    condition
                    conditionDescription
                    variant{
                    family
                    option
                    }                                
                    familyDescription
                    optionDescription
                    price
                    priceType
                    validFrom
                    validTo
                    market                                 
                }
            }
        }`;

    return this.commonService.exportQuery(query, { id: productionPriceListId }, fileName, true, "ProdcutionPriceList");
  }


  fetchPriceTypeList(): Promise<{ id: number; name: string }[]> {
    const query = gql`
       query priceType($id: Int,  $filter:String) {
          admin {
            priceType(id: $id, filter: $filter) {
              
                  id
                  name
                  description
                  isManualType
              
            }
          }
        }
    `;
    return firstValueFrom(this.apollo.query<any>({ query })).then(res => res.data.admin.priceType);
  }

  fetchVariantCombinations(variables?): Promise<variantCodeValues[]> {
    const result$ = this.apollo
      .query<variantCodeValues[]>({
        variables: variables,
        fetchPolicy: 'no-cache',
        query: gql`
             query variantFamilyQuery($order: String, $id: Int,  $paging: Paging,  $filter:String) {
                    admin {
                      variantFamilyOption(order: $order, id: $id,
                      paging: $paging,  filter: $filter) {
                        variantCodeValues{
                            key
                            value
                        }
                      }
                    }
                  }`
      })
      .pipe<variantCodeValues[]>(
        map((result: any) => {
          return result?.data?.admin?.variantFamilyOption
            ?.flatMap((item: any) => item.variantCodeValues || []);
        })

      )
    return lastValueFrom(result$);
  }

  verify(payload): Promise<any> {
    const mutation = `
        mutation ($payload: [ImportPriceListExcel]!) {
            productionPriceListImport {
              verify(payload: $payload){
                priceListId
                componentType
                condition
                conditionDescription
                importedPriceListVariantDetails {
                    variantClass
                    variantFamily
                    variantOption
                    variantOptionId
                    variantFamilyId
                }
                price
                priceType
                validFrom
                validTo
                errorMessage
                market
              }
          }
        }`;

    return this.apollo.mutate({
      mutation: gql(mutation),
      variables: { payload }
    }).toPromise();
  }

  confirm(payload): Promise<any> {
    const mutation = `
        mutation ($payload: [ImportPriceListExcel]!) {
            productionPriceListImport {
              confirm(payload: $payload)
            } 
        }`;

    return this.apollo.mutate({
      mutation: gql(mutation),
      variables: { payload }
    }).toPromise();
  }

  submitPriceListForm(input: PriceListForm): Promise<void> {
    const mutation = gql`
        mutation SubmitPriceListForm($input: PriceListFormInput!) {
          submitPriceListForm(input: $input) {
            list {
              id
              description
              variantCombination
              priceType
              priceValue
              validFrom
              validTo
              priceListTypeId
            }
          }
        }
      `;
    return firstValueFrom(this.apollo.mutate<any>({
      mutation,
      variables: { input }
    })).then(() => { });
  }
}
